import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'

const ROLES = ['owner', 'office_staff', 'project_manager', 'crew_leader', 'laborer', 'store_partner']
const ROLE_LABELS = {
  owner: 'Owner',
  office_staff: 'Office Staff',
  project_manager: 'Project Manager',
  crew_leader: 'Crew Leader',
  laborer: 'Laborer',
  store_partner: 'Store Partner',
}
const BUSINESSES = [{ id: 'pools', label: 'CP Quality Pools' }, { id: 'riverbend', label: 'Riverbend Clearing' }]

export default function TeamPage() {
  const { profile, isOwner } = useAuth()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [message, setMessage] = useState('')
  const [activeTab, setActiveTab] = useState('all')

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    let query = supabase.from('profiles').select('*').order('full_name')
    if (!isOwner) query = query.contains('business_ids', profile?.business_ids || [])
    const { data } = await query
    setUsers(data || [])
    setLoading(false)
  }, [isOwner, profile])

  const swapSeniority = async (userId, direction) => {
    // direction: -1 more senior, +1 more junior
    const ordered = [...users]
      .filter((u) => u.role !== 'store_partner' && (u.seniority_rank || null) !== null)
      .sort((a, b) => (a.seniority_rank || 999) - (b.seniority_rank || 999))
    const myIdx = ordered.findIndex((u) => u.id === userId)
    if (myIdx < 0) return
    const swapIdx = myIdx + direction
    if (swapIdx < 0 || swapIdx >= ordered.length) return
    const me = ordered[myIdx]; const other = ordered[swapIdx]
    const myRank = me.seniority_rank; const otherRank = other.seniority_rank
    await supabase.from('profiles').update({ seniority_rank: otherRank }).eq('id', me.id)
    await supabase.from('profiles').update({ seniority_rank: myRank }).eq('id', other.id)
    fetchUsers()
  }

  const setUserField = async (userId, patch) => {
    await supabase.from('profiles').update(patch).eq('id', userId)
    fetchUsers()
  }

  useEffect(() => { fetchUsers() }, [fetchUsers])

  const filteredUsers = users.filter(u =>
    activeTab === 'all' ? true : u.business_ids?.includes(activeTab) || u.role === 'store_partner'
  )
  const showMsg = (msg) => { setMessage(msg); setTimeout(() => setMessage(''), 4000) }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>TEAM</div>
          <div style={styles.subtitle}>{users.length} members across both businesses</div>
        </div>
        {isOwner && (
          <button onClick={() => { setShowAddForm(true); setEditingUser(null) }} style={styles.addBtn}>+ ADD MEMBER</button>
        )}
      </div>

      {message && <div style={styles.message}>{message}</div>}

      <div style={styles.tabs}>
        {[{ id: 'all', label: 'ALL' }, { id: 'pools', label: 'CP QUALITY POOLS' }, { id: 'riverbend', label: 'RIVERBEND CLEARING' }, { id: 'seniority', label: 'SENIORITY' }].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ ...styles.tab, ...(activeTab === tab.id ? styles.tabActive : {}) }}>
            {tab.label}
          </button>
        ))}
      </div>

      {(showAddForm || editingUser) && isOwner && (
        <UserForm
          user={editingUser}
          onSave={() => {
            fetchUsers()
            setShowAddForm(false)
            setEditingUser(null)
            showMsg(editingUser ? 'User updated.' : 'User added successfully.')
          }}
          onCancel={() => { setShowAddForm(false); setEditingUser(null) }}
        />
      )}

      {loading ? (
        <div style={styles.empty}>Loading team...</div>
      ) : activeTab === 'seniority' ? (
        <SeniorityList
          users={users.filter((u) => u.role !== 'store_partner')}
          isOwner={isOwner}
          onSwap={swapSeniority}
          onSetField={setUserField}
        />
      ) : filteredUsers.length === 0 ? (
        <div style={styles.empty}>No team members found.</div>
      ) : (
        <div style={styles.table}>
          {ROLES.map(role => {
            const group = filteredUsers.filter(u => u.role === role)
            if (group.length === 0) return null
            return (
              <div key={role} style={styles.group}>
                <div style={styles.groupLabel}>
                  {ROLE_LABELS[role].toUpperCase()} {String.fromCodePoint(0x00B7)} {group.length}
                </div>
                {group.map(user => (
                  <UserRow
                    key={user.id}
                    user={user}
                    isOwner={isOwner}
                    currentUserId={profile?.id}
                    onEdit={() => setEditingUser(user)}
                    onDeactivate={() => { fetchUsers(); showMsg('User deactivated.') }}
                    onReactivate={() => { fetchUsers(); showMsg('User reactivated.') }}
                    onSwapSeniority={swapSeniority}
                    onSetField={setUserField}
                  />
                ))}
              </div>
            )
          })}
        </div>
      )}

      {isOwner && (
        <HourlyWageEditor users={users} onUpdate={fetchUsers} />
      )}
    </div>
  )
}

function HourlyWageEditor({ users }) {
  const workers = users.filter(u => u.role !== 'store_partner')
  const [wages, setWages] = useState({})
  const [loaded, setLoaded] = useState(false)
  const { user } = useAuth()

  // Fetch wages from worker_wages table (RLS: owner only)
  useEffect(() => {
    async function loadWages() {
      const { data } = await supabase.from('worker_wages').select('user_id, hourly_wage')
      var wm = {}
      ;(data || []).forEach(function(w) { wm[w.user_id] = w.hourly_wage ?? '' })
      // Ensure all workers have an entry in the local state
      workers.forEach(function(w) { if (wm[w.id] === undefined) wm[w.id] = '' })
      setWages(wm)
      setLoaded(true)
    }
    loadWages()
  }, [users]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleBlur = async (worker) => {
    const raw = wages[worker.id]
    const val = parseFloat(raw)
    if (isNaN(val) && (raw === '' || raw === undefined)) return
    if (isNaN(val)) return
    await supabase.from('worker_wages').upsert({
      user_id: worker.id,
      hourly_wage: val,
      updated_at: new Date().toISOString(),
      updated_by: user.id
    })
  }

  if (!loaded) return null

  return (
    <div style={wageStyles.section}>
      <div style={wageStyles.title}>HOURLY WAGES</div>
      <div style={wageStyles.list}>
        {workers.map(w => {
          const hasWage = wages[w.id] != null && wages[w.id] !== '' && parseFloat(wages[w.id]) > 0
          return (
            <div key={w.id} style={wageStyles.row}>
              <div style={wageStyles.name}>{w.full_name || 'Unnamed'}</div>
              <div style={wageStyles.inputWrap}>
                <span style={wageStyles.dollar}>$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={wages[w.id] ?? ''}
                  placeholder="0.00"
                  onChange={e => setWages(prev => ({ ...prev, [w.id]: e.target.value }))}
                  onBlur={() => handleBlur(w)}
                  style={wageStyles.input}
                />
                {!hasWage && <span style={wageStyles.setLabel}>Set wage</span>}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function availDotStyle(status) {
  if (status === 'available') return { backgroundColor: '#4a8c6a' }
  if (status === 'inactive') return { backgroundColor: '#e05c5c' }
  return { backgroundColor: 'rgba(255,255,255,0.25)' } // off_season / unset
}

function fmtShortDate(s) {
  if (!s) return '—'
  const d = new Date(s + 'T00:00:00')
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
}

function ScheduleEditor({ user, onSetField, onSwapSeniority, onClose }) {
  const [hireDate, setHireDate] = useState(user.hire_date || '')
  const [rank, setRank] = useState(user.seniority_rank || '')
  const [maxDays, setMaxDays] = useState(user.max_days_per_week || 6)
  const [seasonStart, setSeasonStart] = useState(user.season_start || '')
  const [seasonEnd, setSeasonEnd] = useState(user.season_end || '')
  const [availStatus, setAvailStatus] = useState(user.availability_status || 'available')

  const save = async () => {
    await onSetField(user.id, {
      hire_date: hireDate || null,
      seniority_rank: rank ? Number(rank) : null,
      max_days_per_week: Number(maxDays) || 6,
      season_start: seasonStart || null,
      season_end: seasonEnd || null,
      availability_status: availStatus,
    })
    onClose()
  }

  return (
    <div style={schedStyles.overlay} onClick={onClose}>
      <div style={schedStyles.panel} onClick={(e) => e.stopPropagation()}>
        <div style={schedStyles.title}>SCHEDULING — {user.full_name}</div>
        <div style={schedStyles.grid}>
          <div style={schedStyles.field}>
            <label style={schedStyles.label}>HIRE DATE</label>
            <input type="date" value={hireDate} onChange={(e) => setHireDate(e.target.value)} style={schedStyles.input} />
          </div>
          <div style={schedStyles.field}>
            <label style={schedStyles.label}>SENIORITY RANK (1 = MOST SENIOR)</label>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <button type="button" onClick={() => onSwapSeniority(user.id, -1)} style={schedStyles.arrowBtn}>↑</button>
              <input type="number" min="1" value={rank} onChange={(e) => setRank(e.target.value)} style={{ ...schedStyles.input, width: '80px' }} />
              <button type="button" onClick={() => onSwapSeniority(user.id, 1)} style={schedStyles.arrowBtn}>↓</button>
            </div>
          </div>
          <div style={schedStyles.field}>
            <label style={schedStyles.label}>MAX DAYS / WEEK</label>
            <input type="number" min="1" max="6" value={maxDays} onChange={(e) => setMaxDays(e.target.value)} style={schedStyles.input} />
          </div>
          <div style={schedStyles.field}>
            <label style={schedStyles.label}>AVAILABILITY OVERRIDE</label>
            <select value={availStatus} onChange={(e) => setAvailStatus(e.target.value)} style={schedStyles.input}>
              <option value="available">Available</option>
              <option value="off_season">Off-season</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div style={schedStyles.field}>
            <label style={schedStyles.label}>SEASON START</label>
            <input type="date" value={seasonStart} onChange={(e) => setSeasonStart(e.target.value)} style={schedStyles.input} />
          </div>
          <div style={schedStyles.field}>
            <label style={schedStyles.label}>SEASON END</label>
            <input type="date" value={seasonEnd} onChange={(e) => setSeasonEnd(e.target.value)} style={schedStyles.input} />
          </div>
        </div>
        <div style={schedStyles.actions}>
          <button onClick={onClose} style={schedStyles.cancelBtn}>CANCEL</button>
          <button onClick={save} style={schedStyles.saveBtn}>SAVE</button>
        </div>
      </div>
    </div>
  )
}

function SeniorityList({ users, isOwner, onSwap, onSetField }) {
  const [editing, setEditing] = useState(null)
  const ordered = [...users].sort((a, b) => {
    const ra = a.seniority_rank || 999, rb = b.seniority_rank || 999
    if (ra !== rb) return ra - rb
    const ha = a.hire_date || '9999-12-31', hb = b.hire_date || '9999-12-31'
    return ha.localeCompare(hb)
  })
  return (
    <div>
      <div style={styles.groupLabel}>SENIORITY LIST · {ordered.length}</div>
      {ordered.map((u, i) => (
        <div key={u.id} style={{ ...styles.row, alignItems: 'center' }}>
          <div style={{ width: '28px', textAlign: 'center', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '18px', color: '#b48c3c', fontWeight: 700 }}>{u.seniority_rank || '—'}</div>
          <div style={{ ...styles.rowAvatar, width: '32px', height: '32px', fontSize: '13px' }}>{u.full_name?.charAt(0)?.toUpperCase() || '?'}</div>
          <div style={styles.rowMain}>
            <div style={styles.rowName}>
              <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', ...availDotStyle(u.availability_status), marginRight: '8px' }} />
              {u.full_name}
            </div>
            <div style={styles.rowContact}>
              <span>{(u.role || '').replace('_', ' ').toUpperCase()}</span>
              <span>HIRED {fmtShortDate(u.hire_date)}</span>
              <span>SEASON {fmtShortDate(u.season_start)} – {fmtShortDate(u.season_end)}</span>
              <span>MAX {u.max_days_per_week || 6}D/WK</span>
            </div>
          </div>
          {isOwner && (
            <div style={styles.rowActions}>
              <button onClick={() => onSwap(u.id, -1)} style={styles.actionBtn} title="More senior">↑</button>
              <button onClick={() => onSwap(u.id, 1)} style={styles.actionBtn} title="More junior">↓</button>
              <button onClick={() => setEditing(u)} style={styles.actionBtn}>EDIT</button>
            </div>
          )}
          {void i}
        </div>
      ))}
      {editing && (
        <ScheduleEditor
          user={editing}
          onSetField={onSetField}
          onSwapSeniority={onSwap}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}

function UserRow({ user, isOwner, currentUserId, onEdit, onDeactivate, onReactivate, onSwapSeniority, onSetField }) {
  const [confirming, setConfirming] = useState(false)
  const [schedOpen, setSchedOpen] = useState(false)
  const isMe = user.id === currentUserId
  const isInactive = user.is_active === false
  const scheduleEligible = user.role !== 'store_partner'

  const handleDelete = async () => {
    if (!window.confirm('Permanently delete this user? This cannot be undone.')) return
    const resp = await fetch('/api/admin-users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'delete', user_id: user.id }),
    })
    const result = await resp.json()
    if (result.error) {
      alert('Delete failed: ' + result.error)
      return
    }
    onDeactivate()
  }

  const handleDeactivate = async () => {
    const resp = await fetch('/api/admin-users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'deactivate', user_id: user.id }),
    })
    const result = await resp.json()
    if (result.error) {
      alert('Deactivate failed: ' + result.error)
      return
    }
    onDeactivate()
    setConfirming(false)
  }

  const handleReactivate = async () => {
    const resp = await fetch('/api/admin-users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'reactivate', user_id: user.id }),
    })
    const result = await resp.json()
    if (result.error) {
      alert('Reactivate failed: ' + result.error)
      return
    }
    onReactivate()
  }

  return (
    <div style={{ ...styles.row, ...(isInactive ? styles.rowInactive : {}) }}>
      <div style={styles.rowAvatar}>{user.full_name?.charAt(0)?.toUpperCase() || '?'}</div>
      <div style={styles.rowMain}>
        <div style={styles.rowName}>
          {scheduleEligible && (
            <span
              style={{ display: 'inline-block', width: '9px', height: '9px', borderRadius: '50%', ...availDotStyle(user.availability_status), marginRight: '8px' }}
              title={'Availability: ' + (user.availability_status || 'off_season')}
            />
          )}
          {user.full_name || 'Unnamed'}
          {isMe && <span style={styles.meTag}>YOU</span>}
          {isInactive && <span style={styles.inactiveTag}>INACTIVE</span>}
          {scheduleEligible && user.seniority_rank ? <span style={styles.rankTag}>#{user.seniority_rank}</span> : null}
        </div>
        <div style={styles.rowContact}>
          {user.email && <span>{user.email}</span>}
          {user.phone && <span style={styles.rowPhone}>{user.phone}</span>}
          {scheduleEligible && user.hire_date && <span>HIRED {fmtShortDate(user.hire_date)}</span>}
          {scheduleEligible && (user.season_start || user.season_end) && (
            <span>SEASON {fmtShortDate(user.season_start)} – {fmtShortDate(user.season_end)}</span>
          )}
        </div>
      </div>
      <div style={styles.rowBiz}>
        {user.business_ids?.map(b => (
          <span key={b} style={styles.bizChip}>{b === 'pools' ? 'CPP' : 'RBC'}</span>
        ))}
      </div>
      {isOwner && !isMe && (
        <div style={styles.rowActions}>
          {scheduleEligible && <button onClick={() => setSchedOpen(true)} style={styles.actionBtn}>SCHED</button>}
          <button onClick={onEdit} style={styles.actionBtn}>EDIT</button>
          {isInactive ? (
            <button onClick={handleReactivate} style={{ ...styles.actionBtn, color: '#4a8c6a' }}>REACTIVATE</button>
          ) : confirming ? (
            <>
              <button onClick={handleDeactivate} style={{ ...styles.actionBtn, color: '#e05c5c' }}>CONFIRM</button>
              <button onClick={() => setConfirming(false)} style={styles.actionBtn}>CANCEL</button>
            </>
          ) : (
            <>
              <button onClick={() => setConfirming(true)} style={{ ...styles.actionBtn, color: '#e05c5c' }}>DEACTIVATE</button>
              <button onClick={handleDelete} style={{ ...styles.actionBtn, color: '#e05c5c' }}>DELETE</button>
            </>
          )}
        </div>
      )}
      {schedOpen && (
        <ScheduleEditor
          user={user}
          onSetField={onSetField}
          onSwapSeniority={onSwapSeniority}
          onClose={() => setSchedOpen(false)}
        />
      )}
    </div>
  )
}

function UserForm({ user, onSave, onCancel }) {
  const [form, setForm] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    role: user?.role || 'laborer',
    business_ids: user?.business_ids || ['pools'],
    password: '',
    store_name: user?.store_name || '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const toggleBiz = (biz) => {
    const current = form.business_ids
    if (current.includes(biz)) {
      if (current.length === 1) return
      set('business_ids', current.filter(b => b !== biz))
    } else {
      set('business_ids', [...current, biz])
    }
  }

  const handleSave = async () => {
    if (!form.full_name || !form.email) {
      setError('Name and email are required.')
      return
    }
    setSaving(true)
    setError('')

    if (!user) {
      // CREATE via admin API (no session hijack)
      const tempPassword = form.password || 'Pietila2025!'
      const resp = await fetch('/api/admin-users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'create',
          email: form.email,
          password: tempPassword,
          full_name: form.full_name,
          phone: form.phone,
          role: form.role,
          business_ids: form.business_ids,
          store_name: form.role === 'store_partner' ? (form.store_name || form.full_name) : null,
        }),
      })
      const result = await resp.json()
      if (result.error) {
        setError(result.error)
        setSaving(false)
        return
      }

      // Send login email to new user
      if (form.email) {
        var loginUrl = 'https://pietilagroup.com/login'
        var emailBody = 'Hi ' + form.full_name + ',\n\nYou have been added to the Pietila Group platform.\n\nLog in at: ' + loginUrl + '\nEmail: ' + form.email + '\nTemporary Password: ' + tempPassword + '\n\nPlease change your password after logging in.\n\nPietila Group\n(810) 923-6229'
        await fetch('/api/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: form.email,
            subject: 'Your Pietila Group Login',
            body: emailBody,
            business_id: form.business_ids?.includes('riverbend') ? 'riverbend' : 'pools',
          }),
        })
      }

      // Send SMS invite to store partners
      if (form.role === 'store_partner' && form.phone) {
        const loginUrl = 'https://pietilagroup.com/login'
        const smsBody = 'Hi ' + form.full_name + ', you have been added as a store partner with CP Quality Pools / Riverbend Clearing. Log in at: ' + loginUrl + '\nYour email: ' + form.email + '\nTemp password: ' + tempPassword + '\nPlease change your password after logging in.'
        await fetch('/api/send-sms', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: '+1' + form.phone?.replace(/\D/g, ''),
            message: smsBody,
            business_id: form.business_ids?.includes('pools') ? 'pools' : 'riverbend',
          }),
        })
      }
    } else {
      // UPDATE existing user
      const updateData = {
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
        role: form.role,
        business_ids: form.business_ids,
      }
      if (form.role === 'store_partner') {
        updateData.store_name = form.store_name
      }
      const { error: updateError } = await supabase.from('profiles').update(updateData).eq('id', user.id)
      if (updateError) {
        setError(updateError.message)
        setSaving(false)
        return
      }
    }
    setSaving(false)
    onSave()
  }

  return (
    <div style={styles.formCard}>
      <div style={styles.formTitle}>{user ? 'EDIT TEAM MEMBER' : 'ADD TEAM MEMBER'}</div>
      <div style={styles.formGrid}>
        <div style={styles.field}>
          <label style={styles.label}>FULL NAME</label>
          <input value={form.full_name} onChange={e => set('full_name', e.target.value)} style={styles.input} placeholder="First Last" />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>EMAIL</label>
          <input value={form.email} onChange={e => set('email', e.target.value)} style={styles.input} placeholder="email@example.com" type="email" disabled={!!user} />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>PHONE</label>
          <input value={form.phone} onChange={e => set('phone', e.target.value)} style={styles.input} placeholder="8109236229" />
        </div>
        {!user && (
          <div style={styles.field}>
            <label style={styles.label}>TEMP PASSWORD</label>
            <input value={form.password} onChange={e => set('password', e.target.value)} style={styles.input} placeholder="Default: Pietila2025!" />
          </div>
        )}
        <div style={styles.field}>
          <label style={styles.label}>ROLE</label>
          <select value={form.role} onChange={e => set('role', e.target.value)} style={styles.input}>
            {['office_staff', 'project_manager', 'crew_leader', 'laborer', 'store_partner'].map(r => (
              <option key={r} value={r}>{ROLE_LABELS[r]}</option>
            ))}
          </select>
        </div>
        <div style={styles.field}>
          <label style={styles.label}>BUSINESSES</label>
          <div style={styles.bizToggle}>
            {BUSINESSES.map(b => (
              <button
                key={b.id}
                type="button"
                onClick={() => toggleBiz(b.id)}
                style={{ ...styles.bizToggleBtn, ...(form.business_ids.includes(b.id) ? styles.bizToggleBtnActive : {}) }}
              >
                {b.id === 'pools' ? 'CP QUALITY POOLS' : 'RIVERBEND CLEARING'}
              </button>
            ))}
          </div>
        </div>
        {form.role === 'store_partner' && (
          <div style={styles.field}>
            <label style={styles.label}>STORE NAME</label>
            <input value={form.store_name} onChange={e => set('store_name', e.target.value)} style={styles.input} placeholder="e.g. Pools Plus Macomb" />
          </div>
        )}
      </div>
      {error && <div style={styles.error}>{error}</div>}
      <div style={styles.formActions}>
        <button onClick={onCancel} style={styles.cancelBtn}>CANCEL</button>
        <button onClick={handleSave} disabled={saving} style={styles.saveBtn}>
          {saving ? 'SAVING...' : user ? 'SAVE CHANGES' : 'ADD MEMBER'}
        </button>
      </div>
    </div>
  )
}

const styles = {
  container: { maxWidth: '1000px', fontFamily: "'Barlow', sans-serif" },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '32px' },
  title: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '28px', fontWeight: 700, letterSpacing: '3px', color: '#ffffff' },
  subtitle: { fontSize: '15px', color: 'rgba(255,255,255,0.35)', marginTop: '4px' },
  addBtn: { backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', padding: '12px 20px', color: '#0a0f1a', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer' },
  message: { backgroundColor: 'rgba(74,140,106,0.15)', border: '1px solid rgba(74,140,106,0.3)', borderRadius: '3px', padding: '12px 16px', color: '#4a8c6a', fontSize: '14px', marginBottom: '20px' },
  tabs: { display: 'flex', gap: '4px', marginBottom: '24px' },
  tab: { padding: '8px 16px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer' },
  tabActive: { backgroundColor: 'rgba(180,140,60,0.15)', borderColor: 'rgba(180,140,60,0.4)', color: '#b48c3c' },
  table: { display: 'flex', flexDirection: 'column', gap: '24px' },
  group: {},
  groupLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '2px', color: 'rgba(255,255,255,0.25)', marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.05)' },
  row: { display: 'flex', alignItems: 'center', gap: '16px', padding: '16px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '3px', marginBottom: '4px', flexWrap: 'wrap' },
  rowInactive: { opacity: 0.4 },
  rowAvatar: { width: '40px', height: '40px', backgroundColor: 'rgba(180,140,60,0.15)', border: '1px solid rgba(180,140,60,0.2)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: '16px', color: '#b48c3c', flexShrink: 0 },
  rowMain: { flex: 1, minWidth: '200px' },
  rowName: { fontSize: '16px', fontWeight: 500, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px' },
  meTag: { backgroundColor: 'rgba(180,140,60,0.15)', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '3px', padding: '2px 6px', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', letterSpacing: '1px', color: '#b48c3c' },
  inactiveTag: { backgroundColor: 'rgba(224,92,92,0.1)', border: '1px solid rgba(224,92,92,0.2)', borderRadius: '3px', padding: '2px 6px', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', letterSpacing: '1px', color: '#e05c5c' },
  rowContact: { fontSize: '14px', color: 'rgba(255,255,255,0.4)', marginTop: '3px', display: 'flex', gap: '16px', flexWrap: 'wrap' },
  rowPhone: { color: 'rgba(255,255,255,0.3)' },
  rowBiz: { display: 'flex', gap: '6px' },
  bizChip: { backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', padding: '3px 8px', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '1px', color: 'rgba(255,255,255,0.4)' },
  rowActions: { display: 'flex', gap: '8px', flexShrink: 0 },
  actionBtn: { backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', padding: '6px 10px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '1px', cursor: 'pointer' },
  formCard: { backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(180,140,60,0.2)', borderRadius: '4px', padding: '28px', marginBottom: '24px' },
  formTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '16px', fontWeight: 700, letterSpacing: '3px', color: '#b48c3c', marginBottom: '24px' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' },
  field: { display: 'flex', flexDirection: 'column', gap: '8px' },
  label: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '2px', color: 'rgba(255,255,255,0.4)' },
  input: { backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', padding: '12px 14px', color: '#ffffff', fontSize: '15px', fontFamily: "'Barlow', sans-serif", outline: 'none', width: '100%', boxSizing: 'border-box' },
  bizToggle: { display: 'flex', flexDirection: 'column', gap: '8px' },
  bizToggleBtn: { padding: '10px 14px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer', textAlign: 'left' },
  bizToggleBtnActive: { backgroundColor: 'rgba(180,140,60,0.15)', borderColor: 'rgba(180,140,60,0.4)', color: '#b48c3c' },
  error: { backgroundColor: 'rgba(220,60,60,0.1)', border: '1px solid rgba(220,60,60,0.3)', borderRadius: '3px', padding: '12px 16px', color: '#ff6b6b', fontSize: '14px', marginBottom: '16px' },
  formActions: { display: 'flex', gap: '12px', justifyContent: 'flex-end' },
  cancelBtn: { backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', padding: '12px 20px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '13px', letterSpacing: '2px', cursor: 'pointer' },
  saveBtn: { backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', padding: '12px 24px', color: '#0a0f1a', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer' },
  empty: { padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.25)', fontSize: '15px', backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.05)' },
  rankTag: { backgroundColor: 'rgba(180,140,60,0.1)', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '3px', padding: '2px 6px', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', letterSpacing: '1px', color: '#b48c3c' },
}

const schedStyles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, fontFamily: "'Barlow', sans-serif" },
  panel: { backgroundColor: '#0d1420', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '6px', width: 'min(92vw, 560px)', padding: '24px' },
  title: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '14px', fontWeight: 700, letterSpacing: '3px', color: '#b48c3c', marginBottom: '20px' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '20px' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '2px', color: 'rgba(255,255,255,0.4)' },
  input: { backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', padding: '10px 12px', color: '#ffffff', fontSize: '14px', fontFamily: "'Barlow', sans-serif", outline: 'none', boxSizing: 'border-box' },
  arrowBtn: { width: '30px', height: '36px', backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '3px', color: '#b48c3c', cursor: 'pointer', fontSize: '14px' },
  actions: { display: 'flex', gap: '10px', justifyContent: 'flex-end' },
  cancelBtn: { backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', padding: '10px 16px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', letterSpacing: '2px', cursor: 'pointer' },
  saveBtn: { backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', padding: '10px 20px', color: '#0a0f1a', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer' },
}

const wageStyles = {
  section: { marginTop: '32px', backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(180,140,60,0.2)', borderRadius: '4px', padding: '24px' },
  title: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '16px', fontWeight: 700, letterSpacing: '3px', color: '#b48c3c', marginBottom: '16px' },
  list: { display: 'flex', flexDirection: 'column', gap: '8px' },
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '3px' },
  name: { fontSize: '15px', fontWeight: 500, color: '#ffffff', fontFamily: "'Barlow', sans-serif" },
  inputWrap: { display: 'flex', alignItems: 'center', gap: '4px' },
  dollar: { color: 'rgba(255,255,255,0.35)', fontSize: '15px', fontFamily: "'Barlow', sans-serif" },
  input: { backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', padding: '8px 10px', color: '#ffffff', fontSize: '15px', fontFamily: "'Barlow', sans-serif", outline: 'none', width: '90px', boxSizing: 'border-box', textAlign: 'right' },
  setLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '1px', color: '#e05c5c', marginLeft: '8px' },
}
